<script lang="ts">
  /**
   * "What did we use last month?" — two numbers and a difference.
   *
   * There is no chart here on purpose. Thirty days against thirty days is two
   * quantities and the gap between them; a bar chart of two bars is a picture of
   * a subtraction, and it takes longer to read than the subtraction.
   *
   * The figures use `.fig` from `tokens.css` rather than a local font rule. That
   * class already carries `font-variant-numeric: var(--figure-numeric)`, which is
   * `tabular-nums` on an operator surface — so the two figures line up on their
   * decimal without a single alignment rule, and a home surface would get the
   * serif treatment for free. Reinventing it locally is how a portal ends up with
   * figures that no longer match the rest of the estate.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { fig, nice, type TwoNumbersAnswer } from "../answers";

  let { a }: { a: TwoNumbersAnswer } = $props();

  const diff = $derived(a.current !== null && a.previous !== null ? a.current - a.previous : null);
  const pct = $derived(
    a.current !== null && a.previous !== null && a.previous > 0
      ? Math.round(((a.current - a.previous) / a.previous) * 100)
      : null,
  );
  /** The word beside the sign, so the arithmetic never rides on colour alone. */
  const direction = $derived(diff === null ? "no comparison" : diff > 0 ? "more" : diff < 0 ? "less" : "the same");
</script>

<div class="n">
  <div class="n__pair">
    <div class="n__cell">
      <div class="lb">last {a.days} days</div>
      <div class="fig">{fig(a.current)}<span class="fig__u">{a.unit}</span></div>
    </div>

    <div class="n__cell">
      <div class="lb">the {a.days} before</div>
      <div class="fig n__was">{fig(a.previous)}<span class="fig__u">{a.unit}</span></div>
    </div>

    <!-- Amber for a rise, blue for a fall, and neither is inherently good: that
         depends on whether the meter is a pump or a solar array. The glyph and
         the word carry the meaning; the colour only carries the direction. -->
    <div class="n__cell n__cell--diff" class:up={diff !== null && diff > 0} class:down={diff !== null && diff < 0}>
      <div class="lb">difference</div>
      <div class="fig n__diff">
        {#if diff !== null && diff !== 0}
          <Icon name={diff > 0 ? "alert" : "check"} size={16} />
        {/if}
        <span>{diff === null ? "—" : `${diff > 0 ? "+" : ""}${fig(diff)}`}</span>
        <span class="fig__u">{a.unit} {direction}</span>
      </div>
      {#if pct !== null}
        <div class="lb num">{pct > 0 ? "+" : ""}{pct}%</div>
      {/if}
    </div>
  </div>

  {#if a.meters.length > 1}
    <!-- The breakdown is deliberately below and deliberately small. It exists so
         a total nobody recognises can be argued with, not to be read first. -->
    <div class="n__scroll">
      <table>
        <thead>
          <tr><th>Meter</th><th class="r">Last {a.days}d</th><th class="r">Previous</th></tr>
        </thead>
        <tbody>
          {#each a.meters as m (m.entityId)}
            <tr>
              <td>
                <span class="n__nm">{nice(m.entityId)}</span>
                <span class="n__id num">{m.entityId}</span>
              </td>
              <td class="r num">{fig(m.current)}</td>
              <td class="r num sub">{m.previous > 0 ? fig(m.previous) : "—"}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<style>
  .n {
    display: flex;
    flex-direction: column;
    gap: var(--sp-4);
  }
  .n__pair {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sp-3) var(--sp-6);
    align-items: flex-start;
  }
  .n__cell {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }
  /* The previous month is context, not the answer, so it is demoted in weight
     rather than in size — same figure treatment, quieter ink. */
  .n__was {
    color: var(--tx2);
    font-weight: 500;
  }
  .n__cell--diff {
    padding-left: var(--sp-4);
    border-left: 1px solid var(--line);
  }
  .n__diff {
    display: inline-flex;
    align-items: baseline;
    gap: var(--sp-1);
  }
  .n__cell--diff.up .n__diff {
    color: var(--warn);
  }
  .n__cell--diff.down .n__diff {
    color: var(--ok);
  }

  .n__scroll {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--fs-small);
  }
  th {
    text-align: left;
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
    padding: 5px 10px 5px 0;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }
  td {
    padding: 6px 10px 6px 0;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  .r {
    text-align: right;
    padding-right: 0;
  }
  .n__nm {
    display: block;
    font-weight: 700;
    color: var(--tx2);
  }
  .n__id {
    display: block;
    font-size: var(--fs-micro);
    color: var(--mut);
  }
</style>
